"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _APICommands = require("../constants/APICommands");
var _Deferred = _interopRequireDefault(require("../util/Deferred"));
var _Events = require("../constants/Events");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
class AnnotationManager {
  constructor(serviceFactory, isViewSDKPublic = false) {
    this.waitForCommandCompletion = [];
    this._serviceFactory = serviceFactory;
    this._isViewSDKPublic = isViewSDKPublic;
  }
  initEventListener(limited) {
    this.hasLimitedAccess = limited;
    this.initializeEventListener = limited ? Promise.resolve() : this.sendCommand(_APICommands.AnnotationAPICommands.ANNOTATION_EVENT_LISTENER).catch(() => {});
  }
  executeCommand(command, completionPromise) {
    return new Promise((resolve, reject) => {
      command().then(res => {
        completionPromise.resolve();
        resolve(res);
      }).catch(err => {
        completionPromise.resolve();
        reject(err);
      });
    });
  }
  executeCommandAfterWait(command) {
    const waitPromises = [...this.waitForCommandCompletion];
    const completionPromise = new _Deferred.default();
    this.waitForCommandCompletion.push(completionPromise.promise);
    return Promise.all(waitPromises).then(() => this.executeCommand(command, completionPromise));
  }
  getExposedAPIs() {
    const apis = {
      addAnnotations: this.addAnnotations.bind(this),
      updateAnnotation: this.updateAnnotation.bind(this),
      getAnnotations: this.getAnnotations.bind(this),
      deleteAnnotations: this.deleteAnnotations.bind(this),
      selectAnnotation: this.selectAnnotation.bind(this),
      unselectAnnotation: this.unselectAnnotation.bind(this),
      registerEventListener: this.registerEventListener.bind(this),
      addAnnotationsInPDF: this.addAnnotationsInPDF.bind(this),
      removeAnnotationsFromPDF: this.removeAnnotationsFromPDF.bind(this)
    };
    const additionalPublicApis = {
      setConfig: this.setConfig.bind(this),
      startAnnotationMode: this.startAnnotationMode.bind(this),
      endAnnotationMode: this.endAnnotationMode.bind(this)
    };
    const limitedApis = _objectSpread({
      commitUnsavedChanges: this.commitUnsavedChanges.bind(this),
      getPDFBuffer: this.getPDFBuffer.bind(this)
    }, additionalPublicApis);
    if (this._isViewSDKPublic) {
      return _objectSpread(_objectSpread({}, apis), additionalPublicApis);
    }
    return this.hasLimitedAccess ? limitedApis : _objectSpread(_objectSpread({}, apis), limitedApis);
  }
  sendCommand(command, commandData = {}) {
    return this._serviceFactory.getService("CommandReceiverService").executeCommand({
      command,
      commandData
    });
  }

  /**
   * @method
   * @description
   * API to add/import annotation(s) in the PDF file buffer.
   *
   * @param annotations list of annotation data
   * @param options
   */
  addAnnotations(annotations, options = {}) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.ADD_ANNOTATIONS, {
      annotations,
      options
    }));
  }

  /**
   * @method
   * @description
   * API to update the existing annotation in the PDF file buffer.
   *
   * @param annotation annotation data
   */
  updateAnnotation(annotation) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.UPDATE_ANNOTATION, annotation));
  }

  /**
   * @method
   * @description
   * API to get annotations for the file on the basis of the filter provided.
   *
   * @param {Object} filters filter can have annotationIds and pageRange.
   * if annotationIds are provided then all the annotations matching with provided
   * annotation ids will be returned.
   * if pageRange is provided and no annotation Id is provided then all the annotations
   * lies between the startPage and endPage will be returned
   */
  getAnnotations(filters = {}) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.GET_ANNOTATIONS, filters));
  }

  /**
   * @method
   * @description
   * API to delete annotations from the pdf on basis of filter provided.
   *
   * @param {Object} filters filter can have annotationIds and pageRange.
   * if annotationIds are provided then all the annotations matching with provided annotation ids will be deleted
   * if pageRange is provided and no annotation Id is provided then all the annotations lie between the startPage
   * and endPage will be deleted
   */
  deleteAnnotations(filters = {}) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.DELETE_ANNOTATIONS, filters));
  }

  /**
   * @method
   * @description
   * API to select the annotation in pdf file
   *
   * @param id annotation id
   */
  selectAnnotation(id) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.SELECT_ANNOTATION, id));
  }

  /**
   * @method
   * @description
   * API to unselect the annotation. The last selected annotation will be unselected.
   */
  unselectAnnotation() {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.UNSELECT_ANNOTATION));
  }
  registerEventListener(listener, options = {}) {
    let eventsToListen;
    if (options && Array.isArray(options.listenOn) && options.listenOn.length > 0) {
      eventsToListen = options.listenOn;
    } else {
      eventsToListen = Object.keys(_Events.AnnotationEvents);
    }
    this._serviceFactory.getService("EventHandlerService").registerEventListener(eventsToListen, listener);
  }

  /**
   * @method
   * @description
   * API to set config that can be available at comment dropin
   *
   * @param {Object} config config can have properties downloadWithAnnotations,
   * printWithAnnotations etc
   */
  setConfig(config) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.SET_CONFIG, config));
  }

  /**
   * @method
   * @description
   * API to commit user's unsaved changes
   */
  commitUnsavedChanges() {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.COMMIT_UNSAVED_CHANGES));
  }

  /**
   * @method
   * @description
   * API to enter in annotation mode, after which the user can place notes by click
   * in document area or highlight text etc
   *
   * @param mode
   * @param options
   */
  startAnnotationMode(mode, options = {}) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.START_ANNOTATION_MODE, {
      mode,
      options
    }));
  }

  /**
   * @method
   * @description
   * API to end the last started annotation mode.
   */
  endAnnotationMode() {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.END_ANNOTATION_MODE));
  }

  /**
   * @method
   * @description
   * API to add/burn annotations in PDF buffer. It will return the updated PDF buffer.
   * This will burn only the supported annotations provided by the client.
   *
   * @param annotationsList list of annotation data
   */
  addAnnotationsInPDF(annotationsList) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.ADD_ANNOTATIONS_IN_PDF, annotationsList));
  }

  /**
   * @method
   * @description
   * API to separate out PDF and Annotations if there is any already residing within PDF
   * buffer. This API will only remove the annotations that are supported by View SDK
   * currently, all other annotations will remain as it is in PDF buffer.
   */
  removeAnnotationsFromPDF() {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.REMOVE_ANNOTATIONS_FROM_PDF));
  }

  /**
   * @method
   * @description
   * API to provide PDF Buffer
   */
  getPDFBuffer(options = {}) {
    return this.executeCommandAfterWait(() => this.sendCommand(_APICommands.AnnotationAPICommands.GET_PDF_BUFFER, options));
  }
}
exports.default = AnnotationManager;